import { useEffect } from 'react';
import type { ConversationSeed } from '@orborix/shared-types';
import { IconXSmall, IconCheck, IconArrowRight, IconRefresh } from '../icons.js';
import { DIRECTIONS, DirSpark, type Direction } from './directionsData.js';
import { useSheetDrag } from '../../../hooks/useSheetDrag.js';

/**
 * "Direcciones de conversación" (concepto Chispa). Selector (sheet en mobile /
 * popover en desktop) que abre el "+" del composer compartido; el viejo
 * trigger pill del header murió con la adopción de `@orborix/voice-composer`.
 * Reusa los handlers del controller y los iconos genéricos del repo; los
 * glifos/colores por dirección viven en `directionsData`.
 */

// ─── Una card de dirección ──────────────────────────────────────────────────
interface DirCardProps {
  dir: Direction;
  isCurrent: boolean;
  delay: number;
  onPick: (dir: Direction) => void;
}

function DirCard({ dir, isCurrent, delay, onPick }: DirCardProps) {
  const soon = dir.kind === 'soon';
  const cls = ['dir-card', 'dir-bloom', isCurrent ? 'is-current' : '', soon ? 'is-soon' : '']
    .filter(Boolean)
    .join(' ');
  // Duotono por categoría: lima = Juegos, violeta = Temas. El color
  // identitario por dirección (dir.color) queda para el chip del trigger.
  const accent = dir.isGame ? 'var(--lime)' : 'var(--purple)';
  return (
    <button
      type="button"
      className={cls}
      style={{ '--dir-color': accent, animationDelay: `${delay}ms` } as React.CSSProperties}
      onClick={() => {
        if (!soon) onPick(dir);
      }}
      aria-disabled={soon ? true : undefined}
      aria-pressed={isCurrent ? true : undefined}
    >
      <span className="dir-card-tile">
        <dir.Glyph size={26} />
      </span>
      <span className="dir-card-body">
        <span className="dir-card-label">{dir.label}</span>
        <span className="dir-card-sub">{dir.sub}</span>
      </span>

      {soon && <span className="dir-soon-pill">Pronto</span>}
      {!soon && isCurrent && (
        <span className="dir-card-check">
          <IconCheck size={12} />
        </span>
      )}
      {!soon && !isCurrent && dir.kind === 'scenario' && (
        <span className="dir-card-badge" aria-hidden="true">
          <IconArrowRight size={13} />
        </span>
      )}
    </button>
  );
}

// ─── Cuerpo del selector (compartido sheet/popover) ─────────────────────────
// Dos secciones: temas de charla arriba, juegos abajo (misma mecánica de seed).
const topics = DIRECTIONS.filter((d) => !d.isGame);
const games = DIRECTIONS.filter((d) => d.isGame);

interface SelectorBodyProps {
  activeSeed: ConversationSeed | null;
  onPick: (dir: Direction) => void;
  onFree: () => void;
  onClose: () => void;
}

function SelectorBody({ activeSeed, onPick, onFree, onClose }: SelectorBodyProps) {
  return (
    <>
      <div className="dir-head">
        <span className="dir-head-tag">
          <DirSpark size={16} />
          <span className="dir-head-title">Actividades</span>
        </span>
        <button className="dir-x" onClick={onClose} aria-label="Cerrar">
          <IconXSmall size={13} />
        </button>
      </div>

      {activeSeed && (
        <button type="button" className="dir-free" onClick={onFree}>
          <span className="dir-free-ic">
            <IconRefresh size={15} />
          </span>
          <span className="dir-free-body">
            <span className="dir-free-title">Volver a charla libre</span>
            <span className="dir-free-sub">sin dirección · hablamos de lo que surja</span>
          </span>
        </button>
      )}

      {/* Juegos primero (incluye "Una situación"/roleplay); después los temas. */}
      <div className="dir-section-title is-first is-games">
        <span className="tag">Juegos</span>
      </div>
      <div className="dir-grid">
        {games.map((d, i) => (
          <DirCard
            key={d.id}
            dir={d}
            isCurrent={d.seed != null && d.seed === activeSeed}
            delay={40 + i * 45}
            onPick={onPick}
          />
        ))}
      </div>

      <div className="dir-section-title is-topics">
        <span className="tag">Temas</span>
      </div>
      <div className="dir-grid">
        {topics.map((d, i) => (
          <DirCard
            key={d.id}
            dir={d}
            isCurrent={d.seed != null && d.seed === activeSeed}
            delay={40 + (games.length + i) * 45}
            onPick={onPick}
          />
        ))}
      </div>
    </>
  );
}

// ─── Menú (sheet mobile / popover desktop) ──────────────────────────────────
interface DirectionMenuProps {
  variant: 'sheet' | 'popover';
  activeSeed: ConversationSeed | null;
  /** Charla libre pura: en programa/escenario/examen el menú no aplica. */
  onSelectSeed: (seed: ConversationSeed) => void;
  onScenario: () => void;
  /** Dirección "Noticias" (news-service Fase 5): el coach trae una noticia real. */
  onNews: () => void;
  onClear: () => void;
  onClose: () => void;
}

export function DirectionMenu({
  variant,
  activeSeed,
  onSelectSeed,
  onScenario,
  onNews,
  onClear,
  onClose,
}: DirectionMenuProps) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (dir: Direction): void => {
    if (dir.kind === 'scenario') onScenario();
    else if (dir.kind === 'news') onNews();
    else if (dir.kind === 'seed' && dir.seed) onSelectSeed(dir.seed);
  };
  const body = (
    <SelectorBody activeSeed={activeSeed} onPick={pick} onFree={onClear} onClose={onClose} />
  );

  if (variant === 'popover') {
    return (
      <>
        <div className="dir-pop-backdrop" onClick={onClose} />
        <div
          className="dir-popover"
          role="dialog"
          aria-modal="true"
          aria-label="Actividades de conversación"
          onClick={(e) => e.stopPropagation()}
        >
          {body}
        </div>
      </>
    );
  }

  return (
    <div
      className="dir-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Actividades de conversación"
      onClick={onClose}
    >
      <div className="dir-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="dir-handle sheet-grab" {...grabProps} />
        {body}
      </div>
    </div>
  );
}
